import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Howl } from 'howler';
import { useAuth } from '../features/auth';
import { useGacha } from '../features/gacha';
import { useCheckout } from '../features/checkout';
import { useLocker } from '../features/locker';
import { useToast } from '../features/toast';
import TokenIcon from './TokenIcon';
import { WELCOME_MACHINE_TOKEN_ICON, isWelcomeMachine } from '../constants/welcomeMachine';
import { getFreePlaysRemaining } from '../utils/freePlayUtils';

const MACHINE_STYLE = {
  brandColor: '#C12127',
  accent: '#21476B',
  backdrop: '#E9F1FA',
  hopperCapsules: [
    ['#F5F8FF', '#F0463D'], // PonPon coral
    ['#F5F8FF', '#2BA4B4'], // PonPon teal
    ['#F5F8FF', '#F6C34E'], // PonPon gold
    ['#F5F8FF', '#F0463D'], // PonPon coral
    ['#F5F8FF', '#2BA4B4'], // PonPon teal
    ['#F5F8FF', '#F6C34E'], // PonPon gold
  ],
  dropCapsule: ['#F5F8FF', '#2BA4B4'], // PonPon teal
};

const BRAND_COLORS = {
  BANDAI: '#ee161f',
  'TAKARA TOMY': '#0e51a6',
  STASTO: '#f15928',
  BUSHIROAD: '#05923c',
};

const getBrandColor = (brand) => {
  if (!brand) {
    return MACHINE_STYLE.brandColor;
  }
  const normalizedBrand = brand.trim().toUpperCase();
  return BRAND_COLORS[normalizedBrand] ?? MACHINE_STYLE.brandColor;
};

const MachineModal = ({ 
  isOpen, 
  machine, 
  onClose,
}) => {
  const { user, requireLogin } = useAuth();
  const {
    isSpinning,
    currentPrize: prize,
    isPrizeModalOpen,
    startGacha,
    completeAnimation,
    tryAgain,
    setPrizeModalOrigin,
    setIsPrizeModalOpen,
    clearPrize,
  } = useGacha();
  const { registerTokenSpend, revertTokenSpend } = useCheckout();
  const { lockerDecision } = useLocker();
  const { showToast } = useToast();

  const [activeCapsuleIndex, setActiveCapsuleIndex] = useState(0);
  const [capsuleOpened, setCapsuleOpened] = useState(false);
  const [dropComplete, setDropComplete] = useState(true);
  const [tokensInserted, setTokensInserted] = useState(0);
  const [freePlayInserted, setFreePlayInserted] = useState(false);
  const [isAwaitingPrize, setIsAwaitingPrize] = useState(false);
  const [showPrizeChute, setShowPrizeChute] = useState(false);
  const [showFireworks, setShowFireworks] = useState(false);

  const revealTimeoutRef = useRef(null);
  const prizeDisplayRef = useRef(null);
  const spinSoundRef = useRef(null);
  const coinSoundRef = useRef(null);
  const heartbeatSoundRef = useRef(null);
  const revealSoundRef = useRef(null);
  const dropSoundRef = useRef(null);
  const spinSequenceRef = useRef(0);

  const hopperCapsules = useMemo(() => MACHINE_STYLE.hopperCapsules, []);
  const dropCapsule = useMemo(() => MACHINE_STYLE.dropCapsule, []);
  const hasMachineImage = Boolean(machine?.imageUrl);

  // Initialize audio
  useEffect(() => {
    try {
      spinSoundRef.current = new Howl({
        src: ['/audio/spinsound.mp3'],
        volume: 0.7,
        html5: true,
      });
    } catch (e) {
      // no-op if audio failed to init
    }
    return () => {
      try {
        spinSoundRef.current?.unload();
      } catch {}
    };
  }, []);

  useEffect(() => {
    try {
      coinSoundRef.current = new Howl({
        src: ['/audio/coinsound.mp3'],
        volume: 0.6,
        html5: true,
      });
    } catch (e) {
      // no-op if audio failed to init
    }
    return () => {
      try {
        coinSoundRef.current?.unload();
      } catch {}
    };
  }, []);

  useEffect(() => {
    try {
      heartbeatSoundRef.current = new Howl({
        src: ['/audio/heartbeatsound.mp3'],
        volume: 0.5,
        html5: true,
        loop: true,
      });
    } catch (e) {
      // no-op if audio failed to init
    }
    return () => {
      try {
        heartbeatSoundRef.current?.unload();
      } catch {}
    };
  }, []);

  useEffect(() => {
    try {
      revealSoundRef.current = new Howl({
        src: ['/audio/revealsound.mp3'],
        volume: 0.7,
        html5: true,
      });
    } catch (e) {
      // no-op if audio failed to init
    }
    return () => {
      try {
        revealSoundRef.current?.unload();
      } catch {}
    };
  }, []);

  useEffect(() => {
    try {
      dropSoundRef.current = new Howl({
        src: ['/audio/dropsound.mp3'],
        volume: 0.6,
        html5: true,
      });
    } catch (e) {
      // no-op if audio failed to init
    }
    return () => {
      try {
        dropSoundRef.current?.unload();
      } catch {}
    };
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (revealTimeoutRef.current) {
        clearTimeout(revealTimeoutRef.current);
        revealTimeoutRef.current = null;
      }
    };
  }, []);

  // Reset states when modal closes
  useEffect(() => {
    if (!isOpen) {
      if (tokensInserted > 0) {
        revertTokenSpend(tokensInserted);
      }
      setTokensInserted(0);
      setFreePlayInserted(false);
      setIsAwaitingPrize(false);
      setShowPrizeChute(false);
      try {
        heartbeatSoundRef.current?.stop();
      } catch (e) {
        // no-op if audio failed to stop
      }
      
      // CHANGED: Clear prize when modal closes
      // If lockerDecision exists, the prize has become pendingPrize and should be handled via LockerFullModal, so clear currentPrize
      // isPrizeModalOpen means user is viewing prize modal, don't clear
      if (prize && !isPrizeModalOpen) {
        clearPrize();
      }
    }
  }, [isOpen, tokensInserted, revertTokenSpend, prize, lockerDecision, isPrizeModalOpen, clearPrize]);

  useEffect(() => {
    setFreePlayInserted(false);
  }, [machine?.id]);

  // Handle keyboard events
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') {
      return undefined;
    }
    const { body } = document;
    if (!body) {
      return undefined;
    }
    const previousOverflow = body.style.overflow;
    body.style.overflow = 'hidden';
    return () => {
      body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  // Computed values - must be defined before useCallback hooks that use them
  const machineSoldOut = machine ? machine.inventoryAvailable <= 0 : false;
  const userTokens = typeof user?.tokens === 'number' ? user.tokens : 0;
  const availableTokens = userTokens;
  const isFreeMachine = machine?.tokenCost === 0;
  const freePlaysRemaining = getFreePlaysRemaining(user);
  const freePlayLimitReached = isFreeMachine && freePlaysRemaining <= 0;
  const isFullyInserted = machine ?
    (isFreeMachine ? freePlayInserted : tokensInserted >= machine.tokenCost) :
    false;

  // Update active capsule index when spinning
  useEffect(() => {
    if (isSpinning) {
      setActiveCapsuleIndex(Math.floor(Math.random() * hopperCapsules.length));
    }
  }, [isSpinning, hopperCapsules]);

  // Handle prize state changes
  useEffect(() => {
    if (revealTimeoutRef.current) {
      clearTimeout(revealTimeoutRef.current);
      revealTimeoutRef.current = null;
    }
    if (prize) {
      setCapsuleOpened(false);
      setDropComplete(true);
      setIsAwaitingPrize(false);
      setShowPrizeChute(true);
    } else if (!isAwaitingPrize) {
      setCapsuleOpened(false);
      setShowPrizeChute(false);
      try {
        heartbeatSoundRef.current?.stop();
      } catch (e) {
        // no-op if audio failed to stop
      }
    }
  }, [prize, isAwaitingPrize]);

  useEffect(() => {
    if (!isSpinning && !prize) {
      setDropComplete(true);
    }
  }, [isSpinning, prize]);

  // Play heartbeat sound when capsule is jittering
  useEffect(() => {
    const shouldJitter = showPrizeChute && dropComplete && prize && !capsuleOpened && !isPrizeModalOpen;
    try {
      const heartbeatSound = heartbeatSoundRef.current;
      if (heartbeatSound) {
        if (shouldJitter) {
          if (!heartbeatSound.playing()) {
            heartbeatSound.play();
          }
        } else {
          if (heartbeatSound.playing()) {
            heartbeatSound.stop();
          }
        }
      }
    } catch (e) {
      // no-op if audio failed to play/stop
    }
  }, [showPrizeChute, dropComplete, prize, capsuleOpened, isPrizeModalOpen]);
  // CHANGED: Remove token check from knobDisabled - show toast on click instead
  const knobDisabled = Boolean(
    machineSoldOut ||
      freePlayLimitReached ||
      !isFullyInserted
  );
  const tokensNeeded = machine ? machine.tokenCost - tokensInserted : 0;
  // CHANGED: Remove token check from coinSlotDisabled - show toast on click instead
  const coinSlotDisabled = Boolean(
    isSpinning ||
      machineSoldOut ||
      freePlayLimitReached ||
      isFullyInserted ||
      !dropComplete ||
      (!isFreeMachine && tokensNeeded <= 0)
  );

  const knobAxisClassNames = ['machine-knob-axis'];
  if (isSpinning) {
    knobAxisClassNames.push('is-rotating');
  }
  const knobButtonClassNames = ['machine-knob'];
  if (knobDisabled || isSpinning) {
    knobButtonClassNames.push('is-disabled');
  }
  if (isFullyInserted && !isSpinning) {
    knobButtonClassNames.push('is-armed');
  }

  const coinSlotClassNames = ['machine-coin-slot'];
  if (coinSlotDisabled) {
    coinSlotClassNames.push('is-disabled');
  }
  if (machineSoldOut) {
    coinSlotClassNames.push('sold-out');
  }
  if (isFullyInserted) {
    coinSlotClassNames.push('token-inserted');
  }
  const hasCapsuleWaiting = showPrizeChute && dropComplete && prize && !capsuleOpened;
  if (!coinSlotDisabled && !isFullyInserted && !hasCapsuleWaiting) {
    coinSlotClassNames.push('nudge-shake');
  }

  // CHANGED: Inventory progress bar and remaining count UI removed from modal
  // const inventoryPercent = useMemo(() => {
  //   if (!machine || machine.inventoryTotal <= 0) return 0;
  //   return Math.max(0, Math.min(100, (machine.inventoryAvailable / machine.inventoryTotal) * 100));
  // }, [machine]);
  //
  // const lowInventory =
  //   machine &&
  //   !machineSoldOut &&
  //   machine.inventoryAvailable <= Math.max(5, Math.round(machine.inventoryTotal * 0.1));

  const capsuleStyle = useMemo(
    () => hopperCapsules[activeCapsuleIndex] ?? dropCapsule,
    [activeCapsuleIndex, hopperCapsules, dropCapsule]
  );

  const brightPicks = useMemo(() => {
    if (!machine || !Array.isArray(machine.prizePool)) {
      return [];
    }
    const validPrizes = machine.prizePool.filter(
      (prize) => prize && typeof prize.name === 'string' && prize.name.trim().length > 0
    );
    const totalWeight = validPrizes.reduce((sum, prize) => sum + (prize.weight ?? 0), 0);
    return validPrizes
      .sort((a, b) => (b.weight ?? 0) - (a.weight ?? 0))
      .slice(0, 6)
      .map((prize) => {
        const weight = prize.weight ?? 0;
        let weightPercent = totalWeight > 0 ? Math.round((weight / totalWeight) * 100) : 0;
        if (weightPercent <= 1 && weight > 0) {
          weightPercent = '<1';
        }
        return {
          id: prize.id,
          name: prize.name,
          imageUrl:
            typeof prize.imageUrl === 'string' && prize.imageUrl.trim().length > 0
              ? prize.imageUrl.trim()
              : '',
          weight: weight,
          weightPercent: weightPercent,
        };
      });
  }, [machine]);

  const handleInsertToken = useCallback(() => {
    if (!machine || isSpinning || machineSoldOut || !dropComplete) {
      return;
    }
    
    if (!user) {
      if (typeof requireLogin === 'function') {
        requireLogin();
      }
      return;
    }
    // CHANGED: Check token balance and show toast instead of silently failing
    if (user && machine && userTokens < machine.tokenCost) {
      showToast({
        message: 'Not enough tokens to spin!',
        tone: 'error',
      });
      return;
    }
    if (!isFreeMachine && user?.pendingPrize) {
      if (lockerDecision?.pendingPrize) {
        setPrizeModalOrigin(null);
        setIsPrizeModalOpen(true);
      }
      // CHANGED: Removed toast - LockerFullModal will be shown instead
      return;
    }

    if (isFreeMachine) {
      setCapsuleOpened(false);
      setFreePlayInserted(true);
      try {
        const coinSound = coinSoundRef.current;
        if (coinSound) {
          if (coinSound.playing()) coinSound.stop();
          coinSound.play();
        }
      } catch {}
      return;
    }

    const tokensNeeded = machine.tokenCost - tokensInserted;
    if (tokensNeeded <= 0) {
      return;
    }
    if (availableTokens < tokensInserted + 1) {
      showToast({
        message: 'Not enough tokens to insert.',
        tone: 'error',
      });
      return;
    }

    setCapsuleOpened(false);
    const newTokensInserted = tokensInserted + 1;
    setTokensInserted(newTokensInserted);

    registerTokenSpend(1);

    try {
      const coinSound = coinSoundRef.current;
      if (coinSound) {
        if (coinSound.playing()) {
          coinSound.stop();
        }
        coinSound.play();
      }
    } catch (e) {
      // no-op if audio failed to play
    }
  }, [machine, isSpinning, machineSoldOut, dropComplete, user, requireLogin, lockerDecision, isFreeMachine, tokensInserted, availableTokens, registerTokenSpend, setPrizeModalOrigin, setIsPrizeModalOpen, showToast, userTokens]);

  const handleKnobClick = useCallback(() => {
    if (!machine || knobDisabled || isSpinning) return;
    
    if (!user) {
      if (typeof requireLogin === 'function') {
        requireLogin();
      }
      return;
    }
    // CHANGED: Check token balance and show toast instead of silently failing
    if (user && machine && userTokens < machine.tokenCost) {
      showToast({
        message: 'Not enough tokens to spin!',
        tone: 'error',
      });
      return;
    }
    
    if (!isFullyInserted) {
      return;
    }

    try {
      const s = spinSoundRef.current;
      if (s) {
        if (s.playing()) {
          s.stop();
        }
        s.play();
      }
    } catch {}
    setTokensInserted(0);
    setFreePlayInserted(false);
    setCapsuleOpened(false);
    setIsAwaitingPrize(true);
    setShowPrizeChute(true);
    setDropComplete(false);
    setTimeout(() => {
      try {
        const dropSound = dropSoundRef.current;
        if (dropSound) {
          if (dropSound.playing()) {
            dropSound.stop();
          }
          dropSound.play();
        }
      } catch (e) {
        // no-op if audio failed to play
      }
    }, 1100);
    
    const config = {
      machineId: machine.id,
      tokenCost: machine.tokenCost,
    };
    spinSequenceRef.current += 1;
    if (prize) {
      tryAgain(config);
    } else {
      startGacha(config);
    }
  }, [machine, knobDisabled, isSpinning, user, requireLogin, isFullyInserted, prize, tryAgain, startGacha, userTokens, showToast]);

  const handleCapsuleOpen = useCallback(() => {
    if (!capsuleOpened && prize) {
      try {
        heartbeatSoundRef.current?.stop();
      } catch (e) {
        // no-op if audio failed to stop
      }
      try {
        const revealSound = revealSoundRef.current;
        if (revealSound) {
          if (revealSound.playing()) {
            revealSound.stop();
          }
          revealSound.play();
        }
      } catch (e) {
        // no-op if audio failed to play
      }
      setCapsuleOpened(true);
      setShowFireworks(true);
      setTimeout(() => setShowFireworks(false), 1300);
      const invokeReveal = () => {
        const rect = prizeDisplayRef.current
          ? prizeDisplayRef.current.getBoundingClientRect()
          : null;
        if (rect) {
          setPrizeModalOrigin({
            x: rect.left + rect.width / 2 - window.innerWidth / 2,
            y: rect.top + rect.height / 2 - window.innerHeight / 2,
          });
        }
        if (prize) {
          setIsPrizeModalOpen(true);
        }
      };
      setTimeout(() => {
        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
          window.requestAnimationFrame(invokeReveal);
        } else {
          invokeReveal();
        }
      }, 950);
      if (revealTimeoutRef.current) {
        clearTimeout(revealTimeoutRef.current);
      }
      revealTimeoutRef.current = setTimeout(() => {
        setCapsuleOpened(false);
        setShowPrizeChute(false);
        setIsAwaitingPrize(false);
        revealTimeoutRef.current = null;
      }, 650);
    }
  }, [capsuleOpened, prize, setPrizeModalOrigin, setIsPrizeModalOpen]);

  const handleDropAnimationEnd = useCallback(
    (event) => {
      if (event && event.target !== event.currentTarget) {
        return;
      }
      setDropComplete(true);
      setIsAwaitingPrize(true);
      completeAnimation({
        machineId: machine?.id,
        tokenCost: machine?.tokenCost,
      });
    },
    [machine, completeAnimation]
  );

  if (!isOpen || !machine) {
    return null;
  }

  return (
    <div className="machine-modal">
      <div
        className="machine-modal-overlay"
        onClick={onClose}
        role="presentation"
      />
      <div
        className="machine-modal-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="machine-modal-title"
      >
        <section className="rounded-[36px] border border-white/60 bg-white/90 p-6 shadow-xl backdrop-blur-lg">
          <div className="machine-detail-row">
            <div className="machine-experience">
              <div className="machine-unit machine-unit-large relative">
                <div className="machine-top" style={{ borderColor: `${MACHINE_STYLE.accent}33` }}>
                  <div className="machine-brand" style={{ color: getBrandColor(machine.brand) }}>
                    {machine.brand}
                  </div>
                </div>
                <div className="machine-glass">
                  {hasMachineImage ? (
                    <div className="machine-primary-image flex h-full w-full items-center justify-center bg-white/70" aria-hidden="true">
                      <img
                        src={machine.imageUrl}
                        alt={machine.name}
                        className="max-h-full max-w-full object-contain"
                      />
                    </div>
                  ) : (
                    <div className="machine-primary-image flex h-full w-full items-center justify-center bg-white/70">
                      <div className="text-6xl">🎁</div>
                    </div>
                  )}
                </div>
                <div className="machine-body-shell">
                  <button
                    type="button"
                    onClick={handleInsertToken}
                    disabled={coinSlotDisabled}
                    className="machine-insert-panel relative"
                    aria-label={
                      machineSoldOut
                        ? 'Machine is sold out'
                      : isFullyInserted
                        ? (isFreeMachine ? 'Free play ready' : `All ${machine.tokenCost} token${machine.tokenCost === 1 ? '' : 's'} inserted`)
                        : (isFreeMachine ? 'Activate free play' : `Insert token ${tokensInserted + 1} of ${machine.tokenCost}`)
                    }
                    aria-pressed={isFullyInserted}
                  >
                    <div className="machine-label" style={{ color: MACHINE_STYLE.accent }}>
                      {isFreeMachine ? 'FREE SPIN' : 'Insert Token'}
                    </div>
                    <div className={coinSlotClassNames.join(' ')}>
                      {!isFreeMachine && !machineSoldOut && !isFullyInserted && (
                        <span className="machine-coin-icon" aria-hidden="true">
                          <TokenIcon className="!w-[1.15rem] !h-[1.15rem] !min-w-[1.15rem] !min-h-[1.15rem]" alt="" />
                        </span>
                      )}
                      <span className="machine-token-text">
                        {machineSoldOut ? (
                          <strong>Sold out</strong>
                        ) : freePlayLimitReached ? (
                          <strong>TOMORROW</strong>
                        ) : isFullyInserted ? (
                          <strong>{isFreeMachine ? 'READY →' : 'Tap →'}</strong>
                        ) : isFreeMachine ? (
                          <strong>TAP</strong>
                        ) : (
                          <strong>{tokensInserted}/{machine.tokenCost}</strong>
                        )}
                      </span>
                    </div>
                    <div className="machine-slot-rails" aria-hidden="true">
                      <span className="slot-rail slot-rail-left" />
                      <span className="slot-rail slot-rail-right" />
                    </div>
                  </button>
                  <div className={knobAxisClassNames.join(' ')}>
                    <button
                    type="button"
                    onClick={handleKnobClick}
                    disabled={knobDisabled || isSpinning}
                    className={knobButtonClassNames.join(' ')}
                    aria-label="Turn dial to spin the machine"
                  >
                      <span className="machine-knob-face" aria-hidden="true" />
                    </button>
                  </div>
                </div>
                <div className="machine-chute">
                  {(!dropComplete || (dropComplete && !showPrizeChute && isAwaitingPrize)) && (
                    <div
                      className="active-drop-capsule"
                      style={{ '--capsule-top': capsuleStyle[0], '--capsule-bottom': capsuleStyle[1] }}
                      onAnimationEnd={handleDropAnimationEnd}
                    >
                      <div className="active-drop-shell" />
                    </div>
                  )}
                  {showPrizeChute && dropComplete && (!isSpinning || isAwaitingPrize) ? (
                    <div
                      className={`chute-ready ${capsuleOpened ? 'opened' : ''} relative`}
                      style={{ '--capsule-top': capsuleStyle[0], '--capsule-bottom': capsuleStyle[1] }}
                    >
                      <button
                        type="button"
                        onClick={handleCapsuleOpen}
                        disabled={!prize || capsuleOpened}
                        className={`chute-capsule-trigger ${capsuleOpened ? 'opened' : ''}`}
                      >
                        <div className={`chute-capsule-shell ${capsuleOpened ? 'open' : ''} ${!capsuleOpened && prize ? 'capsule-jitter' : ''}`} />
                      </button>
                      {capsuleOpened && prize ? (
                        <div className="chute-prize-display reveal" ref={prizeDisplayRef}>
                          {(typeof prize?.imageUrl === 'string' && prize.imageUrl.trim().length > 0) ||
                          prize?.tokenBonus ||
                          isWelcomeMachine(machine) ? (
                            <img
                              src={prize?.imageUrl || WELCOME_MACHINE_TOKEN_ICON}
                              alt={prize.name || 'Prize'}
                              className="max-h-24 max-w-[70%] object-contain"
                            />
                          ) : (
                            <span className="text-4xl sm:text-5xl">{prize?.image || '🎁'}</span>
                          )}
                        </div>
                      ) : null}
                      {showFireworks ? (
                        <div className="fireworks" aria-hidden="true">
                          <span className="spark" />
                          <span className="spark" />
                          <span className="spark" />
                          <span className="spark" />
                          <span className="spark" />
                          <span className="spark" />
                          <span className="spark" />
                          <span className="spark" />
                          <span className="spark" />
                          <span className="spark" />
                          <span className="spark" />
                          <span className="spark" />
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            <aside className="machine-info-panel">
              <div className="machine-info-header">
                <div className="machine-tag-row">
                  {machine.tags
                    .filter((tag) => !['featured'].includes(tag.toLowerCase()))
                    .map((tag) => (
                      <span key={tag} className="machine-tag">
                        {tag}
                      </span>
                    ))}
                </div>
                <h3 className="machine-info-title" id="machine-modal-title">
                  {machine.name}
                </h3>
                {machine.tagline ? (
                  <p className="machine-info-subtitle">{machine.tagline}</p>
                ) : null}
              </div>

              {/* CHANGED: Inventory progress and capsules remaining removed from machine modal */}
              {/* <div className="machine-info-inventory" aria-label="Inventory status">
                <div
                  className="inventory-progress"
                  role="progressbar"
                  aria-valuenow={machine.inventoryAvailable}
                  aria-valuemin={0}
                  aria-valuemax={machine.inventoryTotal}
                >
                  <div
                    className={`inventory-progress-fill ${
                      machineSoldOut ? 'sold-out' : lowInventory ? 'low-stock' : ''
                    }`}
                    style={{ width: `${inventoryPercent}%` }}
                  />
                </div>
                <p className="inventory-count">
                  {machine.inventoryAvailable} of {machine.inventoryTotal} capsules remaining
                </p>
              </div> */}

              <div className="machine-info-prizes">
                <h4>What's Inside</h4>
                <div className="prize-grid">
                  {brightPicks.map(({ id, name, imageUrl, weightPercent }) => (
                    <div key={id || name} className="prize-card">
                      <div className="prize-card-thumb">
                        {imageUrl ? (
                          <img src={imageUrl} alt={name} />
                        ) : (
                          <div className="prize-card-placeholder" />
                        )}
                      </div>
                      {!isWelcomeMachine(machine) ? (
                        <span className="prize-card-weight">{weightPercent}%</span>
                      ) : null}
                      <div className="prize-card-name">{name}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="machine-modal-action">
                <button
                  type="button"
                  onClick={onClose}
                  className="machine-modal-back"
                >
                  <span aria-hidden="true">←</span>
                  Back to homepage
                </button>
              </div>
            </aside>
          </div>
        </section>
      </div>
    </div>
  );
};

export default MachineModal;
